import { UnitOption } from "../constants/units";

// Environment variables injected at runtime
declare global {
  interface Window {
    _env_?: {
      REACT_APP_API_URL?: string;
      REACT_APP_ENVIRONMENT?: string;
      REACT_APP_TENANT?: string;
      REACT_APP_SENTRY_DSN?: string;
      REACT_APP_GOOGLE_ANALYTICS_ID?: string;
      REACT_APP_GTM_ID?: string;
      REACT_APP_HOTJAR_ID?: string;
      REACT_APP_VERSION?: string;
      REACT_APP_GRANTS_TURNSTILE_SITE_KEY?: string;
      REACT_APP_GRANTS_TURNSTILE_ENABLED?: string;
      REACT_APP_KN_REGISTRATION_V2_ENABLED?: string;
    };
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

export interface CategoryDto {
  id: string;
  name: string;
  hotelId: string;
  parentId?: string;
  options: CategoryOptionDto[];
}

export interface CategoryOptionDto {
  id: string;
  name: string;
  defaultNotes?: string;
  defaultUnitPrice: number;
  unit?: UnitOption | null;
  personsPerRoom?: number; // Tylko dla ACCOMMODATION
  vatRate: number;
  optionType: "accommodation" | "gastronomy" | "conference" | "additional";
  isActive: boolean;
  sortOrder: number;
  maxQuantity?: number; // null or 0 = brak ograniczenia
  hotelId: string; // Zmiana z categoryId na hotelId
  // Profitroom mapping fields (tylko dla accommodation)
  profitroomOfferId?: string;
  singleBeds?: number;
  doubleBeds?: number;
  convertibleBeds?: number;
  isAutoBreakfast?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCategoryOptionDto {
  name?: string;
  defaultNotes?: string;
  defaultUnitPrice?: number;
  unit?: UnitOption | null;
  personsPerRoom?: number; // Tylko dla ACCOMMODATION
  vatRate?: number;
  optionType?: "accommodation" | "gastronomy" | "conference" | "additional";
  isActive?: boolean;
  sortOrder?: number;
  maxQuantity?: number; // null or 0 = brak ograniczenia
  hotelId?: string;
  // Profitroom mapping fields (tylko dla accommodation)
  profitroomOfferId?: string;
  singleBeds?: number;
  doubleBeds?: number;
  convertibleBeds?: number;
  isAutoBreakfast?: boolean;
}

// Nowe typy dla hardcoded kategorii
export type OptionType =
  | "accommodation"
  | "gastronomy"
  | "conference"
  | "additional";

export interface CategoryOptionsByType {
  [key: string]: CategoryOptionDto[];
}

// Toast system types
export interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "info" | "undo" | "warning";
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number; // in milliseconds, undefined = no auto-dismiss
}

export interface PendingDeletion {
  itemId: string;
  item: any;
  originalIndex: number;
  deletedAt: number;
  timerId: NodeJS.Timeout;
  toastId: string;
}

// Offer Status Enum
export enum OfferStatus {
  DRAFT = "draft", // Projekt oferty - w trakcie tworzenia
  PUBLISHED = "published", // Oferta opublikowana - zamknięta do edycji, publiczna
  ACCEPTED = "accepted", // Oferta zaakceptowana przez klienta
  REJECTED = "rejected", // Oferta odrzucona przez klienta
}

// Offer and Plan Item types
export interface DayPlanItem {
  id: string;
  timeFrom?: string;
  timeTo?: string;
  quantity: number;
  unitPrice: number;
  customTitle?: string;
  personsCountPerRoom?: number;
  notes?: string;
  occupancyType?: "pairs" | "singles_shared" | "singles_alone";
  categoryOption?: CategoryOptionDto;
  pendingDeletion?: boolean;
  parent?: DayPlanItem;
  parentId?: string;
  children?: DayPlanItem[];
  finalPrice: number;
  sortOrder: number;
  discountType?: "percentage" | "amount";
  discountValue?: number;
}

// Specific type for accommodation items with required fields and proper typing
export interface AccommodationItem extends DayPlanItem {
  occupancyType: "pairs" | "singles_shared" | "singles_alone";
  categoryOption: CategoryOptionDto; // Required for accommodation items
  personsCountPerRoom?: number; // Number of persons actually assigned to this room (for singles_shared)
}

export interface OfferDay {
  id: string;
  date: string;
  dayNumber: number;
  pairs: number;
  singles: number;
  singlesAlone: number;
  planItems?: DayPlanItem[];
}

// Discount override types
export interface CustomDiscount {
  name: string;
  percentage: number;
}

export interface DiscountOverrides {
  personDiscount: number;
  stayLengthDiscount: number;
  weekdayDiscount: number;
  customDiscounts?: CustomDiscount[];
}

export interface DiscountPreview {
  auto: {
    personDiscount: number;
    stayLengthDiscount: number;
    weekdayDiscount: number;
    totalDiscount: number;
    personDiscountDetails?: {
      appliedTier: { minPersons: number; discount: number };
      personCounts: number[];
      averagePersons: number;
      allTiers: Array<{ minPersons: number; discount: number }>;
    };
    stayLengthDiscountDetails?: {
      appliedTier: { minNights: number; discount: number };
      numberOfNights: number;
      allTiers: Array<{ minNights: number; discount: number }>;
    };
    weekdayDiscountDetails?: {
      weekdayRate: number;
      hasWeekend: boolean;
      totalNights: number;
      isWeekdayOnly: boolean;
    };
  };
  override: DiscountOverrides | null;
  isDivergent: boolean;
}

export interface Offer {
  id: string;
  title: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  description?: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfRooms: number;
  numberOfGuests: number;
  netValue: number;
  appliedPersonDiscount: number;
  appliedStayLengthDiscount: number;
  totalDiscount: number;
  discountOverrides?: DiscountOverrides | null;
  discountsLocked?: boolean;
  status: OfferStatus;
  publicToken: string;
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
  offerDays: OfferDay[];
  components?: any[];
  createdBy?: any;
  hotel?: any;
  views?: any[];
  accommodationMarkupType?: "percentage" | "amount";
  accommodationMarkupValue?: number;
  financialSummary?: {
    totalNet: number;
    totalVat: number;
    totalGross: number;

    // Cena na osobę
    pricePerPerson: {
      personsCount: number; // Maksymalna liczba osób
      netPerPerson: number; // Cena netto na osobę
      grossPerPerson: number; // Cena brutto na osobę
    };

    appliedDiscounts: {
      personDiscount: number;
      stayLengthDiscount: number;
      weekdayDiscount: number;
      totalGlobalDiscount: number;
      customDiscounts?: CustomDiscount[];
      discountsLocked?: boolean;
      itemDiscountsTotal: number;
      totalDiscountAmount: number;
      personDiscountDetails?: {
        appliedTier: {
          minPersons: number;
          discount: number;
        };
        personCounts: number[];
        averagePersons: number;
        allTiers: Array<{ minPersons: number; discount: number }>;
      };
      stayLengthDiscountDetails?: {
        appliedTier: {
          minNights: number;
          discount: number;
        };
        numberOfNights: number;
        allTiers: Array<{ minNights: number; discount: number }>;
      };
      weekdayDiscountDetails?: {
        weekdayRate: number;
        hasWeekend: boolean;
        totalNights: number;
        isWeekdayOnly: boolean;
      };
    };

    groupedByVatAndCategory: Record<
      string,
      {
        categoryName: string;
        vatRate: number;
        originalNet: number;
        discountAmount: number;
        net: number;
        vat: number;
        gross: number;
        items: Array<{
          dayNumber: number;
          customTitle?: string;
          categoryOptionName?: string;
          quantity: number;
          unitPrice: number;
          discount?: {
            type: "percentage" | "amount";
            value: number;
          };
          finalPrice: number;
          // Informacje o osobach w pokoju (tylko dla noclegów)
          personsPerRoom?: number; // Pojemność pokoju (z CategoryOption)
          personsCountPerRoom?: number; // Za ile osób się płaci (z DayPlanItem)
        }>;
      }
    >;

    totalWith23Vat: number;
  };
}

// Profitroom integration types
export interface ProfitRoomAvailabilityParams {
  checkIn: string;
  checkOut: string;
  single: number;
  pairs: number;
}

export interface ProfitRoomOffer {
  offerId: string;
  offerName?: string;
  crmScore: number;
  pricing: {
    amount: number;
    currency: string;
    originalAmount?: number;
    simulatedAmount?: number;
    recentLowestAmount?: number;
    loyaltyAmount?: number;
  };
  discounts: Array<{
    amount: number;
    availableTo?: string;
    isMobile: boolean;
  }>;
  occupancyOption: {
    id: string;
    arrangementId?: string;
    extraBedIds: string[];
  };
  stayPeriod: {
    from: string;
    to: string;
  };
  occupancy: {
    adults: number;
    children: number[];
  };
}

export interface RoomTypeAvailability {
  roomId: string;
  roomName: string;
  maxOccupancy: number;
  availableCount: number;
  offers: ProfitRoomOffer[];
}

export interface ProfitRoomAvailabilityResponse {
  hotel: {
    code: string;
    name: string;
  };
  searchParams: {
    checkIn: string;
    checkOut: string;
    totalPersons: number;
    singleBedPersons: number;
    occupancyQueries: Array<{
      adults: number;
      children: number[];
    }>;
  };
  roomTypes: RoomTypeAvailability[];
  lastUpdated: string;
}

export interface OccupancyInput {
  totalPersons: number;
  pairsCount: number;
  singlesCount: number;
}

export interface RoomCombination {
  roomType: string;
  roomName: string;
  count: number;
  pricePerRoom: number;
  totalPrice: number;
  categoryOptionId: string;
}

export interface RoomSuggestion {
  combination: RoomCombination[];
  totalRooms: number;
  totalPrice: number;
  efficiency: number;
}

// Analytics types
export interface OfferWithAnalytics extends Offer {
  view_count: number;
  last_viewed_at: string | null;
  total_session_duration: number;
  unique_sessions: number;
  has_analytics: boolean;
  mainOfferPublicToken?: string; // Public token of the main offer (for links)
  // Versioning metadata
  hasVersions?: boolean;
  hasDraft?: boolean;
  draftVersion?: {
    id: string;
    status: string;
    updatedAt: Date;
  };
  publishedVersionsCount?: number;
  latestVersionNumber?: number;
  latestVersionDate?: Date;
}

export interface OfferAnalyticsSummary {
  offer_id: string;
  view_count: number;
  last_viewed_at: Date | null;
  unique_sessions: number;
  total_session_duration: number;
  average_session_duration: number;
  has_analytics: boolean;
}

export interface OfferAnalyticsSession {
  session_id: string;
  started_at: Date;
  ended_at: Date | null;
  total_duration: number;
  device_type: string;
  browser_name: string;
  browser_version: string;
  operating_system: string;
  screen_resolution: string;
  viewport_size: string;
  ip_address: string;
  user_agent: string;
  referrer: string;
  page_views: number;
  consent_analytics: boolean;
}

export interface OfferAnalyticsSessionsResponse {
  sessions: OfferAnalyticsSession[];
  total_count: number;
}

// Section Time Analytics Types
export interface SectionTimeAnalytics {
  sectionName: string;
  totalTimeMs: number;
  averageTimeMs: number;
  percentage: number;
  sessionCount: number;
}

export interface OfferSectionTimeAnalyticsResponse {
  totalOfferViewTime: number;
  sectionAnalytics: SectionTimeAnalytics[];
}

export interface SessionSectionTime {
  sectionName: string;
  timeMs: number;
  percentage: number;
}

// Batch Availability Types (for new accommodation interface redesign - Phase 2)
export interface BatchAvailabilityStrategy {
  type: "daily" | "full-term" | "extended-term";
  originalCheckIn: string;
  originalCheckOut: string;
  actualCheckIn?: string;
  actualCheckOut?: string;
  reason?: "restrictions" | "api-error" | "no-availability";
}

export interface RestrictionInfo {
  originalCheckIn: string;
  originalCheckOut: string;
  actualCheckIn: string;
  actualCheckOut: string;
  message: string;
  profitroomData?: {
    totalNights: number;
    requestedTerm: string;
    actualTerm: string;
    roomPricing: {
      [roomName: string]: {
        profitroomId: number;
        totalPriceFromProfitroom: number;
        pricePerNightCalculated: number;
        availableCount: number;
        currency: string;
      };
    };
  };
}

export interface BatchAvailabilityResponse {
  offerId: string;
  days: DayAvailability[];
  minAvailability: {
    [roomId: string]: MinAvailabilityItem;
  };
  status: "SUCCESS" | "PROFITROOM_NOT_CONFIGURED" | "PROFITROOM_API_ERROR";
  lastUpdated: string;
  strategy?: BatchAvailabilityStrategy;
  priceSource?: "daily" | "full-term" | "manual";
  hasRestrictions?: boolean;
  restrictionInfo?: RestrictionInfo;
}

export interface DayAvailability {
  date: string;
  dayId: string;
  rooms: DayRoomAvailability[];
}

export interface DayRoomAvailability {
  categoryOptionId: string;
  name: string;
  availableCount: number;
  pricing: {
    single?: { net: number; gross: number };
    double?: { net: number; gross: number };
    triple?: { net: number; gross: number };
  };
}

export interface MinAvailabilityItem {
  name: string;
  minCount: number;
  singleBeds: number;
  doubleBeds: number;
  convertibleBeds: number;
  dailyAvailability: {
    [date: string]: number;
  };
  pricing: {
    [date: string]: {
      single?: { net: number; gross: number };
      double?: { net: number; gross: number };
      triple?: { net: number; gross: number };
    };
  };
}

// Pricing Strategies Types
export interface PricingStrategy {
  type: string;
  enabled: boolean;
  reason: string;
  explanation: string;
  coefficient: number;
  pricingSuggestion?: {
    avgOriginalPrice: number;
    suggestedPrice: number;
    discountAmount: number;
    discountPercent: number;
  };
}

export interface PricingStrategiesResponse {
  strategies: PricingStrategy[];
}

// File Management Types
export interface File {
  id: string;
  displayName: string;
  originalFileName: string;
  fileSize: number;
  fileType: string;
  fileUrl: string;
  description?: string;
  category: string;
  tags?: string[];
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  uploadedBy: {
    id: string;
    name: string;
  };
}

export interface OfferFile {
  id: string;
  customDescription?: string;
  sortOrder: number;
  attachedAt: string;
  file: File;
  attachedBy: {
    id: string;
    name: string;
  };
}

// Subscription Types
export type PaymentSource = "stripe" | "bank_transfer" | "manual";

/**
 * Pending proforma invoice details for bank transfer payment
 */
export interface PendingProforma {
  proformaNumber: string;
  amountGross: number;
  currency: string;
  dueDate: string | null;
  bankAccountNumber: string;
  recipientName: string;
  pdfUrl: string;
  contactEmail?: string;
}

export interface SubscriptionStatus {
  id: string;
  planName: string;
  planId: string;
  status:
    | "incomplete"
    | "trialing"
    | "active"
    | "past_due"
    | "unpaid"
    | "canceled"
    | "suspended"
    | "expired";
  enabledModules: string[];
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  stripeCustomerId: string | null;
  paymentSource: PaymentSource;
  pendingProforma?: PendingProforma;
  billingModel?: 'legacy' | 'ordering';
  /** When false, BillingRenewalCronJob skips proforma and sends expiration notice instead. Absent = treat as true (backward compat). */
  autoRenewal?: boolean;
}

export interface BillingPortalUrl {
  url: string;
}
